import { useEffect, type RefObject } from 'react';

interface UseEscapeKeyOptions {
  enabled: boolean;
  onEscape: () => void;
  /**
   * Listen in the capture phase so a dialog takes Escape before the page-level
   * listeners (mobile drawer of `DashboardLayout`), which then never see it.
   */
  capture?: boolean;
  /**
   * The dialog owning Escape. When the focus is in another modal stacked over
   * it (delete confirmation over « Clôturer »), Escape is left to that one.
   */
  containerRef?: RefObject<HTMLElement | null>;
}

interface UseEscapeKeyReturn {
  isListening: boolean;
}

export function useEscapeKey({
  enabled,
  onEscape,
  capture = false,
  containerRef,
}: UseEscapeKeyOptions): UseEscapeKeyReturn {
  useEffect(() => {
    if (!enabled) return;

    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        const container = containerRef?.current;
        const active = document.activeElement;
        const activeModal = active instanceof HTMLElement ? active.closest('[aria-modal="true"]') : null;
        if (container && activeModal && activeModal !== container && !activeModal.contains(container)) return;
        event.stopPropagation();
        onEscape();
      }
    };

    document.addEventListener('keydown', handler, capture);
    return () => {
      document.removeEventListener('keydown', handler, capture);
    };
  }, [enabled, onEscape, capture, containerRef]);

  return { isListening: enabled };
}
